import type { AiDecisionStatsGroup } from '../../../api/DigiobcanApi';
import { latency, percent } from '../../../lib/aiDecisions';
import { __ } from '../../../utils';
import { Box, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  groups: AiDecisionStatsGroup[];
  loading?: boolean;
};

const Tile: React.FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Stack
    gap={0.5}
    sx={({ palette, shape }) => ({
      border: `1px solid ${palette.divider}`,
      borderRadius: `${shape.borderRadius}px`,
      backgroundColor: palette.background.paper,
      px: 2,
      py: 1.5,
      minWidth: 0,
    })}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography
        sx={{
          fontSize: 12,
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {hint}
      </Typography>
    ) : null}
  </Stack>
);

/** Podiel zhody z behov, pri ktorých sa zhoda dá určiť. */
const agreementRate = (agreed: number, disagreed: number) =>
  agreed + disagreed ? agreed / (agreed + disagreed) : undefined;

/**
 * Pás čísel nad zoznamom behov AI. Sčítava skupiny z
 * `GET /admin/ai-decisions-stats` - ten ide s **tými istými filtrami** ako
 * zoznam, takže čísla hovoria o tom, čo je v tabuľke pod nimi, nie o všetkom.
 *
 * Rozpad po modeloch a verziách promptu tu bol ako tabuľka pod dlaždicami
 * a je preč - opakoval to, čo sa dá vyfiltrovať, a zoznam pod ním odtláčal.
 * Celkové číslo cez dva rôzne klasifikátory je priemer, ktorý nepopisuje ani
 * jeden z nich, preto sa na porovnanie modelov filtruje (Typ, Model, Verzia
 * promptu) a dlaždice potom hovoria o jednom.
 */
const AiDecisionsStats: React.FC<Props> = ({ groups, loading }) => {
  const total = React.useMemo(
    () =>
      groups.reduce(
        (sum, group) => ({
          runs: sum.runs + group.runs,
          errors: sum.errors + group.errors,
          agreed: sum.agreed + group.humanAgreed,
          disagreed: sum.disagreed + group.humanDisagreed,
          latencyWeighted:
            sum.latencyWeighted + (group.avgLatencyMs ?? 0) * group.runs,
          latencyRuns:
            sum.latencyRuns +
            (group.avgLatencyMs === undefined ? 0 : group.runs),
        }),
        {
          runs: 0,
          errors: 0,
          agreed: 0,
          disagreed: 0,
          latencyWeighted: 0,
          latencyRuns: 0,
        },
      ),
    [groups],
  );

  const dash = loading ? '…' : '-';
  const avgLatency = total.latencyRuns
    ? Math.round(total.latencyWeighted / total.latencyRuns)
    : undefined;
  const rate = agreementRate(total.agreed, total.disagreed);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
      }}
    >
      <Tile label={__('Behy')} value={total.runs ? String(total.runs) : dash} />
      <Tile
        label={__('Chyby')}
        value={total.runs ? String(total.errors) : dash}
        hint={
          total.runs
            ? (percent(total.errors / total.runs) ?? undefined)
            : undefined
        }
      />
      <Tile
        label={__('Priemerná latencia')}
        value={latency(avgLatency) ?? dash}
      />
      <Tile
        label={__('Zhoda s úradníkom')}
        value={percent(rate) ?? dash}
        hint={
          total.agreed + total.disagreed
            ? `${total.agreed} ${__('zhoda')} · ${total.disagreed} ${__('prepísané')}`
            : __('Zatiaľ nie je s čím porovnať')
        }
      />
    </Box>
  );
};

export default AiDecisionsStats;
